'use client';
import React, { useState, useCallback } from 'react';
import { Search, MapPin, Briefcase, Clock, DollarSign, ArrowRight, Building2, TrendingUp, ChevronRight, BookmarkPlus, Bookmark } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import HomeButton from '@/components/HomeButton';

interface JobListing {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  category: string;
  posted: string;
  applications: number;
  is_featured: boolean;
  logo_color: string;
  description: string;
}

const JOBS: JobListing[] = [
  { id: '1', title: 'Senior Software Engineer', company: 'Safaricom PLC', location: 'Nairobi', salary: 'KES 150K–200K', type: 'Full-time', category: 'Technology', posted: '2 days ago', applications: 45, is_featured: true, logo_color: '#00C853', description: 'Join our engineering team to build next-generation telecom solutions.' },
  { id: '2', title: 'Marketing Manager', company: 'KCB Bank', location: 'Nairobi', salary: 'KES 80K–120K', type: 'Full-time', category: 'Marketing', posted: '1 day ago', applications: 23, is_featured: false, logo_color: '#00BCD4', description: 'Lead marketing campaigns for East Africa\'s largest bank.' },
  { id: '3', title: 'Registered Nurse (RN)', company: 'Aga Khan Hospital', location: 'Mombasa', salary: 'KES 60K–90K', type: 'Full-time', category: 'Healthcare', posted: '3 days ago', applications: 28, is_featured: true, logo_color: '#FF4081', description: 'Provide quality patient care in our state-of-the-art facility.' },
  { id: '4', title: 'Accountant CPA', company: 'Deloitte East Africa', location: 'Nairobi', salary: 'KES 100K–140K', type: 'Full-time', category: 'Finance', posted: '5 days ago', applications: 67, is_featured: false, logo_color: '#7C4DFF', description: 'Audit and advisory services for top-tier clients across East Africa.' },
  { id: '5', title: 'Sales Representative', company: 'Unilever Kenya', location: 'Kisumu', salary: 'KES 40K–60K', type: 'Full-time', category: 'Sales', posted: '1 day ago', applications: 12, is_featured: false, logo_color: '#FFB300', description: 'Drive FMCG sales in the Western Kenya region.' },
  { id: '6', title: 'UI/UX Designer', company: 'Andela Kenya', location: 'Nairobi (Remote)', salary: 'KES 90K–130K', type: 'Remote', category: 'Design', posted: '4 hours ago', applications: 8, is_featured: true, logo_color: '#FF6D00', description: 'Design beautiful digital products for global clients.' },
  { id: '7', title: 'Logistics Coordinator', company: 'DHL Kenya', location: 'Nairobi', salary: 'KES 50K–70K', type: 'Full-time', category: 'Logistics', posted: '6 days ago', applications: 34, is_featured: false, logo_color: '#FFCC00', description: 'Coordinate international and domestic shipments.' },
  { id: '8', title: 'Data Analyst', company: 'Equity Bank', location: 'Nairobi', salary: 'KES 70K–100K', type: 'Full-time', category: 'Technology', posted: '2 days ago', applications: 19, is_featured: false, logo_color: '#E91E63', description: 'Analyse financial data to drive business decisions.' },
];

const CATEGORIES = ['All Categories', 'Technology', 'Marketing', 'Healthcare', 'Finance', 'Sales', 'Design', 'Logistics'];
const JOB_TYPES = ['All Types', 'Full-time', 'Part-time', 'Remote', 'Contract'];
const LOCATIONS = ['All Locations', 'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret'];

export default function JobsLandingPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All Categories');
  const [jobType, setJobType] = useState('All Types');
  const [location, setLocation] = useState('All Locations');
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  const filtered = JOBS.filter(j => {
    const matchSearch = !search || j.title.toLowerCase().includes(search.toLowerCase()) || j.company.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All Categories' || j.category === category;
    const matchType = jobType === 'All Types' || j.type === jobType;
    const matchLoc = location === 'All Locations' || j.location.includes(location);
    return matchSearch && matchCat && matchType && matchLoc;
  });

  const toggleSave = useCallback((id: string) => {
    setSavedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Hero */}
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #071a2e 0%, #0a1a0d 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #00BCD4 0%, transparent 50%), radial-gradient(circle at 70% 30%, #00C853 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="flex items-center justify-between mb-4">
              <HomeButton />
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600"
                style={{ background: 'rgba(0,188,212,0.15)', color: '#00BCD4', border: '1px solid rgba(0,188,212,0.3)' }}>
                <TrendingUp size={12} /> {(12340).toLocaleString()} Active Jobs
              </div>
              <Link href="/create-listing?type=job" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-700"
                style={{ background: '#9C27B0', color: '#fff' }}>
                <Briefcase size={13} /> Post Job
              </Link>
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Find Your Next <span style={{ color: '#00BCD4' }}>Career Move</span>
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Browse thousands of jobs from top employers across Kenya. Apply in seconds.
            </p>
            <div className="flex gap-2 max-w-lg mx-auto">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Job title or company…"
                  className="w-full pl-9 pr-3 py-3.5 rounded-2xl text-sm outline-none"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
              </div>
              <div className="relative">
                <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
                <select value={location} onChange={e => setLocation(e.target.value)}
                  className="pl-8 pr-3 py-3.5 rounded-2xl text-sm outline-none appearance-none"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }}>
                  {LOCATIONS.map(l => <option key={l} value={l} style={{ background: '#0D1526' }}>{l}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Category pills */}
        <div className="px-4 py-3 sticky top-0 z-20"
          style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCategory(c)}
                className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: category === c ? 'rgba(0,188,212,0.15)' : 'rgba(255,255,255,0.04)', color: category === c ? '#00BCD4' : 'rgba(255,255,255,0.55)', border: `1px solid ${category === c ? 'rgba(0,188,212,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {c}
              </button>
            ))}
            <div className="flex-shrink-0 w-px h-5 mx-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
            {JOB_TYPES.slice(1).map(t => (
              <button key={t} onClick={() => setJobType(jobType === t ? 'All Types' : t)}
                className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: jobType === t ? 'rgba(0,200,83,0.15)' : 'rgba(255,255,255,0.04)', color: jobType === t ? '#00C853' : 'rgba(255,255,255,0.55)', border: `1px solid ${jobType === t ? 'rgba(0,200,83,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#00BCD4', fontWeight: 700 }}>{filtered.length}</span> jobs found
            </span>
            <Link href="/login"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
              style={{ background: '#00BCD4', color: '#000' }}>
              Post a Job <ArrowRight size={12} />
            </Link>
          </div>

          <div className="flex flex-col gap-3">
            {filtered.map(job => (
              <div key={job.id}
                className="p-4 rounded-2xl transition-all"
                style={{ background: job.is_featured ? 'rgba(0,188,212,0.05)' : 'rgba(255,255,255,0.03)', border: `1px solid ${job.is_featured ? 'rgba(0,188,212,0.2)' : 'rgba(255,255,255,0.07)'}` }}>
                <div className="flex items-start gap-3">
                  {/* Company logo placeholder */}
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-white font-800 text-base"
                    style={{ background: job.logo_color + '22', border: `1px solid ${job.logo_color}33`, color: job.logo_color }}>
                    {job.company[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-700 text-white">{job.title}</h3>
                          {job.is_featured && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-md font-700"
                              style={{ background: 'rgba(255,179,0,0.15)', color: '#FFB300' }}>Featured</span>
                          )}
                        </div>
                        <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{job.company}</p>
                      </div>
                      <button onClick={() => toggleSave(job.id)} className="flex-shrink-0 p-1.5 rounded-lg transition-all"
                        style={{ background: savedIds.has(job.id) ? 'rgba(0,188,212,0.15)' : 'rgba(255,255,255,0.05)' }}>
                        {savedIds.has(job.id)
                          ? <Bookmark size={14} fill="#00BCD4" style={{ color: '#00BCD4' }} />
                          : <BookmarkPlus size={14} style={{ color: 'rgba(255,255,255,0.4)' }} />}
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      <div className="flex items-center gap-1">
                        <MapPin size={11} style={{ color: 'rgba(255,255,255,0.35)' }} />
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{job.location}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <DollarSign size={11} style={{ color: 'rgba(255,255,255,0.35)' }} />
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{job.salary}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={11} style={{ color: 'rgba(255,255,255,0.35)' }} />
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{job.posted}</span>
                      </div>
                      <span className="text-[10px] px-2 py-0.5 rounded-lg font-600"
                        style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>{job.type}</span>
                    </div>
                    <p className="text-xs mt-2 line-clamp-1" style={{ color: 'rgba(255,255,255,0.35)' }}>{job.description}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{job.applications} applicants</span>
                      <Link href={`/listing/jobs/${job.id}`}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-700 transition-all"
                        style={{ background: 'rgba(0,188,212,0.15)', color: '#00BCD4', border: '1px solid rgba(0,188,212,0.25)' }}>
                        View & Apply <ChevronRight size={11} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-8 p-6 rounded-3xl text-center"
            style={{ background: 'linear-gradient(135deg, rgba(0,188,212,0.1), rgba(0,200,83,0.1))', border: '1px solid rgba(0,188,212,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">See All {(12340).toLocaleString()} Jobs</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Sign up free to apply, save jobs, and get alerts for new listings.</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link href="/login"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
                style={{ background: '#00BCD4', color: '#000' }}>
                <Briefcase size={16} /> Find a Job
              </Link>
              <Link href="/login"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
                style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}>
                <Building2 size={16} /> Post a Job
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
